import { useState } from 'react';
import { Plus, Search, Globe } from 'lucide-react';
import { SiteCard } from './SiteCard';
import { AddSiteModal } from '../sites/AddSiteModal';
import { useSites } from '../../hooks/useSites';
import { useTeamContext } from '../../hooks/useTeamContext';

export function Dashboard() {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { activeTeamId, activeTeam, hasMultipleTeams } = useTeamContext();
  const { sites, isLoading, error, refetch } = useSites({
    teamId: activeTeamId,
  });

  const filteredSites = sites.filter(
    site =>
      site.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      site.url.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="py-8">
      <div className="container-content">
        {/* Header Section */}
        <div className="section-dark rounded-card p-8 mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-heading-1 font-bold text-white mb-2">
                {activeTeam && hasMultipleTeams ? `${activeTeam.name} Sites` : 'Your Sites'}
              </h1>
              <p className="text-body text-white/70">
                {activeTeam && hasMultipleTeams
                  ? `Sites shared within the ${activeTeam.name} team`
                  : 'Manage and monitor accessibility compliance for your websites'}
              </p>
            </div>
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="btn-primary flex items-center gap-2"
            >
              <Plus size={20} />
              <span>Add Site</span>
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search
            className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-medium"
            size={20}
          />
          <input
            type="text"
            placeholder="Search sites..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="input pl-12"
          />
        </div>

        {/* Sites Grid */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin-slow w-8 h-8 border-2 border-coral border-t-transparent rounded-full" />
          </div>
        ) : error ? (
          <div className="card text-center py-12">
            <p className="text-status-error mb-4">{error}</p>
            <button onClick={refetch} className="btn-secondary">
              Try Again
            </button>
          </div>
        ) : filteredSites.length === 0 ? (
          <div className="card text-center py-12">
            <Globe size={48} className="mx-auto text-gray-medium mb-4" />
            <h2 className="text-heading-3 font-semibold mb-2">
              {searchQuery ? 'No sites found' : 'No sites yet'}
            </h2>
            <p className="text-body text-gray-medium mb-6">
              {searchQuery
                ? 'Try adjusting your search query'
                : 'Add your first website to start scanning for accessibility issues'}
            </p>
            {!searchQuery && (
              <button
                onClick={() => setIsAddModalOpen(true)}
                className="btn-primary"
              >
                <Plus size={20} className="mr-2" />
                Add Your First Site
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredSites.map(site => (
              <SiteCard key={site.siteId} site={site} />
            ))}
          </div>
        )}
      </div>

      {/* Add Site Modal */}
      <AddSiteModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={() => {
          setIsAddModalOpen(false);
          refetch();
        }}
      />
    </div>
  );
}
